import { all, call, delay, put, select, take, takeEvery, takeLatest, race } from 'redux-saga/effects'
import { Config } from '../config'
import { selectSession } from '../selectors/session'
import { SessionState } from '../reducers/session'
import { handleUnauthorizedRequestError } from './helper'
import {
  EVENT_REQUEST,
  EVENT_REQUEST_FAILURE,
  EVENT_REQUEST_SUCCESS,
  EVENT_POLLING_START,
  EVENT_POLLING_STOP,
  fetchEventsAction
} from '../actions/events'
import { EventServerClient, EventObject } from '../util/events'
import { RootState } from '../reducers/root'
import { createNotificationsFromEvents } from '../util/eventNotifications'
import { addNotification } from '../actions/notification'


export function* eventsSaga(): Generator {
  yield all([
    takeEvery(EVENT_REQUEST, fetchEventsRequest),
    takeLatest(EVENT_POLLING_START, eventPollingSaga),
  ])
}

export function* fetchEventsRequest(action: fetchEventsAction): any {
  const session: SessionState = yield select(selectSession)
  let result
  const client = new EventServerClient(Config.eventServerURL, session.idToken)
  try {
    result = yield call(client.fetchEvents, action.filter, action.options)
  } catch (error) {
    const handled: boolean = yield call(handleUnauthorizedRequestError, error, action)
    if (handled) return

    yield put({ type: EVENT_REQUEST_FAILURE, txId: (action as any).txId, error })
    return
  }
  yield put({ type: EVENT_REQUEST_SUCCESS, txId: (action as any).txId, reset: action.options?.reset, ...result })
}

export function* eventPollingSaga(): Generator {
  // Track which event IDs we've already notified about
  const notifiedEventIds: Set<string> = new Set()

  // Ticket #822 - Numéros de prélèvement déjà annoncés par une notification
  // « X prélèvements non importés » : les événements d'un même import peuvent
  // tomber sur plusieurs cycles de polling, on ne ré-annonce pas un
  // prélèvement déjà signalé.
  const notifiedLegacySampleIds: Set<string> = new Set()

  while (true) {
    try {
      const session: SessionState = yield select(selectSession)
      const lastViewedAt: Date | null = yield select((state: RootState) => state.events.lastViewedAt)
      const existingEventIds: string[] = Object.keys(yield select((state: RootState) => state.events.byId))

      const client = new EventServerClient(Config.eventServerURL, session.idToken)

      // Fetch events created after the last viewed timestamp
      const options: any = { limit: 100 }
      if (lastViewedAt) {
        options.after = lastViewedAt.toISOString()
      }

      const result: { events: EventObject[], total: number } = yield call(client.fetchEvents, undefined, options)

      // Find new events that we haven't seen or notified about
      const newEvents = result.events.filter((event: EventObject) =>
        !existingEventIds.includes(event.id) && !notifiedEventIds.has(event.id)
      )

      // Mark the whole batch as notified
      for (const event of newEvents) {
        notifiedEventIds.add(event.id)
      }

      // Create and dispatch notifications for new events. Le lot est traité en
      // bloc plutôt qu'événement par événement : certains types se regroupent
      // en un message unique (cf. "X prélèvements non importés", ticket #822).
      const notifications = createNotificationsFromEvents(newEvents, notifiedLegacySampleIds)

      for (const notification of notifications) {
        yield put(addNotification(notification))
      }

      yield put({ type: EVENT_REQUEST_SUCCESS, ...result })
    } catch (error) {
      // Silently handle polling errors - we'll retry on next interval
      console.error('Event polling error:', error)
    }

    // Wait for the next polling interval or stop action
    const raceResult: { stopped?: any } = yield race({
      delay: delay(Config.eventPollInterval * 1000),
      stopped: take(EVENT_POLLING_STOP)
    })

    if (raceResult.stopped) {
      break
    }
  }
}
